import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { NgxPermissionsService } from 'ngx-permissions';
import { Observable, of } from 'rxjs';
import { catchError, first, tap } from 'rxjs/operators';
import { environment } from '@App/environments/environment';
import { AuthResponse } from '../entities/auth/auth-response.model';
import { UserPermissions } from '../entities/auth/permissions.enum';
import { LoginData } from '../entities/auth/login-data.model';
import { ResetPassword } from '../entities/auth/reset-password.model';
import { AuthHttpService } from './auth-http.service';
import { AuthStateService } from './auth-state.service';
import { EndpointsService } from './endpoints.service';
import {
  loadAuthDataSuccess,
  refreshTokensSuccess,
  removeAuthDataSuccess,
  updatePermissionsFromRefresh,
} from './store/auth/actions/auth.actions';
import { selectAuthData } from './store/auth/selectors/auth.selectors';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends AuthStateService {
  private loginUrl = `${environment.loginUrl}/auth/login`;
  private resetPasswordUrl = `${environment.loginUrl}/auth/resetPassword`;
  private confirmResetPasswordUrl = `${environment.loginUrl}/auth/confirmResetPassword`;

  constructor(
    private router: Router,
    permissionsService: NgxPermissionsService,
    store: Store,
    private endpointsService: EndpointsService,
    private authHttpService: AuthHttpService,
  ) {
    super(store, permissionsService);
  }

  login(loginData: LoginData): Observable<AuthResponse> {
    return this.authHttpService.login(this.loginUrl, loginData);
  }

  onLoginSuccess(response: AuthResponse) {
    const { user, auth, endpoint } = response;
    this.endpoints = [...this.endpointsService.filterAvailableEndpoints(endpoint)];
    const currentEndpoint = this.endpoints[0];
    [this.user, this.tokens] = [user, auth];
    this.authChange.next(true);
    this.store.dispatch(
      loadAuthDataSuccess({
        authData: {
          user,
          endpoints: endpoint,
          currentEndpoint,
          tokens: auth,
        },
      }),
    );

    const prevUrl = this.router.parseUrl(this.originalUrl || '');
    const prevQueryParams = prevUrl.queryParams;

    this.router.navigate([this.resolveLoginTargetRoute()], {
      queryParams: {
        ...prevQueryParams,
        freshLogin: 'true',
      },
    });
    this.originalUrl = null;
  }

  private resolveLoginTargetRoute(): string {
    if (this.hasAccessToAdminPanel()) {
      return '/company-selection';
    }
    const previousRoute = this.originalUrl?.split('?')[0];
    if (previousRoute) {
      return previousRoute;
    }
    if (this.endpoints.length > 1 || !this._currentEndpoint$.value) {
      return '/company-selection';
    }
    return `${this._currentEndpoint$.value.id}/models`;
  }

  hasAccessToAdminPanel() {
    return this.permissionsService.getPermission(UserPermissions.CAN_ACCESS_ADMIN_PANEL);
  }

  refreshTokens() {
    return this.authHttpService
      .refreshTokens(this.endpointPath.value || '', this.tokens?.refreshToken as string)
      .pipe(
        tap((tokens) => {
          this.store.dispatch(refreshTokensSuccess({ tokens }));

          // If backend includes updated permissions, dispatch action to update them
          if (tokens.endpoints) {
            this.store.dispatch(
              updatePermissionsFromRefresh({
                endpoints: tokens.endpoints,
              }),
            );
          }
        }),
        catchError((error) => {
          this.onLogout();
          return of(error);
        }),
      );
  }

  logout() {
    return this.authHttpService.logout(
      this.endpointPath.value as string,
      this.tokens?.accessToken as string,
    );
  }

  onLogout() {
    this.store.dispatch(removeAuthDataSuccess());
    this.store
      .select(selectAuthData)
      .pipe(first())
      .subscribe(() => {
        [this.user, this.tokens, this.endpoints] = [null, null, []];
        this._currentEndpoint$.next(null);
        this.authChange.next(false);
        this.endpointPath.next(null);
        this.router.navigate(['/login']);
      });
  }

  resetPassword(email: string) {
    return this.authHttpService.resetPassword(this.resetPasswordUrl, email);
  }

  changePassword(old: string, _new: string) {
    const [endpointPath, accessToken] = [this.endpointPath.value, this.tokens?.accessToken];
    return this.authHttpService.changePassword(
      endpointPath as string,
      accessToken as string,
      old,
      _new,
    );
  }

  confirmResetPassword(data: ResetPassword) {
    return this.authHttpService.confirmResetPassword(this.confirmResetPasswordUrl, data);
  }

  getUser() {
    return { ...this.user };
  }

  isAuth() {
    return !!this.user;
  }

  getUserRoles() {
    return this.getCurrentEndpoint()?.userRoles;
  }

  getAvailableEndpoints() {
    return this.endpointsService.filterAvailableEndpoints(this.endpoints);
  }

  isUserInOtherCompanies() {
    return this.endpoints.length > 1;
  }
}
